import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ─── Auto-refresh interceptor ────────────────────────────────────────────────
// On any 401 from /api/*, try POST /api/auth/refresh once and replay the request.
let _refreshPromise = null;
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error?.config;
    const status = error?.response?.status;
    if (
      status === 401 &&
      original &&
      !original._retried &&
      typeof original.url === 'string' &&
      original.url.includes('/api/') &&
      !original.url.includes('/auth/login') &&
      !original.url.includes('/auth/refresh') &&
      !original.url.includes('/auth/me')
    ) {
      original._retried = true;
      try {
        if (!_refreshPromise) {
          _refreshPromise = axios.post(`${API}/auth/refresh`, {}, { withCredentials: true })
            .finally(() => { _refreshPromise = null; });
        }
        await _refreshPromise;
        // Replay the original request with credentials so the new cookie is sent
        original.withCredentials = true;
        return axios(original);
      } catch (refreshErr) {
        return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  }
);

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true
      });
      setUser(response.data);
    } catch (error) {
      setUser(false);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(
      `${API}/auth/login`,
      { email, password },
      { withCredentials: true }
    );
    setUser(response.data);
    return response.data;
  };

  const register = async (email, password, name, phone, address) => {
    const response = await axios.post(
      `${API}/auth/register`,
      { email, password, name, phone, address },
      { withCredentials: true }
    );
    // OTP flow - don't set user yet
    return response.data;
  };

  const verifyOTP = async (email, otp) => {
    const response = await axios.post(
      `${API}/auth/verify-otp`,
      { email, otp },
      { withCredentials: true }
    );
    setUser(response.data);
    return response.data;
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    setUser(false);
  };

  const value = {
    user,
    loading,
    login,
    register,
    verifyOTP,
    logout,
    isAuthenticated: !!user && user !== false
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
